import React from 'react';
import { Scissors, Heart, Star, Users } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ScrollToTop from '../components/ScrollToTop';

interface TeamMember {
  name: string;
  role: string;
  experience: string;
  image: string;
  specialties: string[];
}

const teamMembers: TeamMember[] = [
  {
    name: "Dee",
    role: "Master Barber & Founder",
    experience: "15+ years",
    image: "/assets/dee.png",
    specialties: ["Classic Cuts", "Beard Styling", "Hot Towel Shaves"]
  }
];

const About: React.FC = () => {
  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <ScrollToTop />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-24 md:pb-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-gray-100 mb-6 font-['Instrument_Serif']">
            Our Story
          </h1>
          <div className="w-24 h-1 bg-amber-500 mb-8 mx-auto md:mx-0"></div>
          <p className="text-center md:text-left text-xl text-gray-700 dark:text-gray-300 max-w-2xl">
            Discover the journey behind 3D Barbers and meet the team that makes it all possible.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="prose prose-lg dark:prose-invert mx-auto">
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-6">
              Five minute walk from Shepards Bush Market Station, 3D on busy the Uxbridge Road, has become recognised salon in West London. Our mission is to uphold quality care for each clients needs and provide an excellent customer service. We have talented barbers and stylists with skills and experience to cut all types of afro and european hair textures.
            </p>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              The shop was opened in 2009 and has since been refurbished to create relaxing and friendly environment for our customers. Our trusted, fast growing customer base often return for their professional service that suits their personality and style.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900 dark:text-gray-100">
            Our Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <Scissors className="h-8 w-8 text-amber-500" />
                <h3 className="text-xl font-semibold ml-3 text-gray-900 dark:text-gray-100">Craftsmanship</h3>
              </div>
              <p className="text-gray-700 dark:text-gray-300">
                We believe in the power of precision and attention to detail in every cut we deliver.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <Heart className="h-8 w-8 text-amber-500" />
                <h3 className="text-xl font-semibold ml-3 text-gray-900 dark:text-gray-100">Passion</h3>
              </div>
              <p className="text-gray-700 dark:text-gray-300">
                Our love for the art of barbering drives us to continuously improve and innovate.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <Users className="h-8 w-8 text-amber-500" />
                <h3 className="text-xl font-semibold ml-3 text-gray-900 dark:text-gray-100">Community</h3>
              </div>
              <p className="text-gray-700 dark:text-gray-300">
                We create a welcoming space where everyone feels valued and respected.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900 dark:text-gray-100">
            Meet Our Founder
          </h2>
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="w-full md:w-1/2">
              <div className="bg-gray-50 dark:bg-gray-900 rounded-xl overflow-hidden shadow-lg">
                <div className="h-[400px] overflow-hidden">
                  <img
                    src={teamMembers[0].image}
                    alt={teamMembers[0].name}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-gray-100">{teamMembers[0].name}</h3>
                  <p className="text-amber-500 font-medium mb-2">{teamMembers[0].role}</p>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">Experience: {teamMembers[0].experience}</p>
                  <div className="space-y-2">
                    {teamMembers[0].specialties.map((specialty, idx) => (
                      <div key={idx} className="flex items-center">
                        <Star className="h-4 w-4 text-amber-500 mr-2" />
                        <span className="text-gray-700 dark:text-gray-300">{specialty}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full md:w-1/2">
              <div className="prose prose-lg dark:prose-invert">
                <p className="text-xl text-gray-700 dark:text-gray-300 mb-6">
                  With over 15 years of experience in the industry, Dee has established 3D Unisex Afro Euro Salon. His journey began with a passion for the art of barbering and a commitment to excellence in every cut.
                </p>
                <p className="text-xl text-gray-700 dark:text-gray-300 mb-6">
                  Under Dee's leadership, 3D Barbers has grown from a small local shop to a recognized establishment in West London, known for its exceptional service and attention to detail. His expertise in classic cuts, beard styling, and hot towel shaves has earned him a loyal clientele.
                </p>
                <p className="text-xl text-gray-700 dark:text-gray-300">
                  Dee's dedication to his craft and commitment to customer satisfaction continues to drive the success of 3D Barbers, making it a premier destination for quality grooming services in the community.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About; 